import { SPORT_TERMINOLOGY } from './app/lib/constants';
import { pluralTerm } from './app/lib/helpers/term.helper';
import { ISelectionTarget, Sport, SportTerminology } from './app/lib/types';

export const APP_TITLE = 'Max Analytics';
export const SUPPORT_EMAIL = 'support@maxanalytics.ca';
// Marketing-site contact section; #mea-contact anchors to the form on that page.
export const MARKETING_CONTACT_URL = 'https://maxanalytics.ca/contact.html#mea-contact';

export const ENABLE_DEKSTOP = true;
export const ENABLE_MOBILE = true;

export const ENABLE_SENTRY = true;
export const ENABLE_LOGROCKET = true;
export const ENABLE_CHAT = true;
export const HAS_PHI = false;

export const HAS_USERS = true;
export const OPEN_REGISTRATION = HAS_USERS && true;

export const HAS_COMPANIES = true;
export const COMPANY_TERM = 'association';

export const HAS_PROJECTS = true;
export const PROJECT_TERM = 'assessment';

export const ROLES = {
  CHECK_IN: 'CHECK_IN',
  SCORING: 'SCORING',
  PLAYER_NAMES: 'PLAYER_NAMES',
  REPORTS: 'REPORTS',
  CONFIGURATION: 'CONFIGURATION',
  CONFIG_DRILLS: 'CONFIG_DRILLS',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  COMMUNICATION: 'COMMUNICATION'
};

export enum MemberListType {
  SUPER = "Super Users",
  ASSESSMENT = "Assessment Users",
  AGE_GROUP = "Age Group Users"
}

export const MEMBER_GROUP_ROLES = {
  [MemberListType.SUPER]: [ROLES.CONFIGURATION, ROLES.CONFIG_DRILLS, ROLES.USER_MANAGEMENT, ROLES.COMMUNICATION],
  [MemberListType.ASSESSMENT]: [ROLES.CONFIGURATION, ROLES.SCORING, ROLES.CHECK_IN, ROLES.COMMUNICATION],
  [MemberListType.AGE_GROUP]: [ROLES.CONFIGURATION, ROLES.USER_MANAGEMENT, ROLES.PLAYER_NAMES, ROLES.REPORTS, ROLES.COMMUNICATION]
}

// A lookup dictionary of comparable permissions. Eg. the first entry shows what roles at what role group have equality to the role+role group for the first two keys
export const ROLE_SUBSTITUTES = {
  [MemberListType.SUPER]: {
    [ROLES.CONFIG_DRILLS]: {
      [MemberListType.SUPER]: [ROLES.CONFIGURATION] // eg. SUPER.CONFIGURATION also grants access to SUPER.CONFIG_DRILLS
    }
  },
  [MemberListType.ASSESSMENT]: {
    [ROLES.CONFIGURATION]: {
      [MemberListType.SUPER]: [ROLES.CONFIGURATION]
    },
    [ROLES.COMMUNICATION]: {
      [MemberListType.SUPER]: [ROLES.COMMUNICATION] // SUPER.COMMUNICATION grants ASSESSMENT.COMMUNICATION
    }
  },
  [MemberListType.AGE_GROUP]: {
    [ROLES.CONFIGURATION]: {
      [MemberListType.SUPER]: [ROLES.CONFIGURATION],
      [MemberListType.ASSESSMENT]: [ROLES.CONFIGURATION]
    },
    [ROLES.PLAYER_NAMES]: {
      [MemberListType.AGE_GROUP]: [ROLES.REPORTS]
    },
    [ROLES.USER_MANAGEMENT]: {
      [MemberListType.SUPER]: [ROLES.USER_MANAGEMENT]
    },
    [ROLES.COMMUNICATION]: {
      [MemberListType.SUPER]: [ROLES.COMMUNICATION],
      [MemberListType.ASSESSMENT]: [ROLES.COMMUNICATION] // ASSESSMENT.COMMUNICATION grants AGE_GROUP.COMMUNICATION
    }
  }
}

export const EXCLUSIVE_ROLES = {
  [MemberListType.SUPER]: [ROLES.CONFIG_DRILLS],
  [MemberListType.ASSESSMENT]: [ROLES.SCORING, ROLES.CHECK_IN],
  [MemberListType.AGE_GROUP]: [ROLES.PLAYER_NAMES, ROLES.REPORTS]
}

export const ROLES_TABLE_KEYS = {
  [ROLES.CHECK_IN]: 'checkin',
  [ROLES.SCORING]: 'evaluator',
  [ROLES.PLAYER_NAMES]: 'playerNames',
  [ROLES.REPORTS]: 'reports',
  [ROLES.CONFIGURATION]: 'config',
  [ROLES.CONFIG_DRILLS]: 'configDrills',
  [ROLES.USER_MANAGEMENT]: 'users',
  [ROLES.COMMUNICATION]: 'communication'
}

export const ROLE_LABELS: { [key: string]: string } = {
  CHECK_IN: 'Check-In',
  SCORING: 'Scoring',
  PLAYER_NAMES: `${SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE]} Names`,
  REPORTS: 'Reports',
  CONFIGURATION: 'Config',
  CONFIG_DRILLS: 'Skills & Drills',
  USER_MANAGEMENT: 'Users',
  COMMUNICATION: 'Communication'
};

export const ROLE_DESCRIPTIONS: { [key: string]: string } = {
  CHECK_IN: 'Allow user to check players in and out of assessment sessions',
  SCORING: 'Allow user to score and evaluate player performance during assessments',
  PLAYER_NAMES: `Allow user to view ${SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE].toLowerCase()} names and personal information`,
  REPORTS: 'Allow user to view and export assessment reports and analytics',
  CONFIGURATION: 'Allow user to modify system settings and configuration options',
  CONFIG_DRILLS: 'Allow user to create, edit, and manage skills, drills, and practice plans',
  USER_MANAGEMENT: 'Allow user to invite new users and manage user permissions',
  COMMUNICATION: 'Allow user to access the communication panel and send messages'
};

export const ROUTE_ROLES = {
  PUBLIC: 'PUBLIC',
  AUTHORIZED: 'AUTHORIZED',
  ...ROLES
};

export const HOME_ROUTE = '/';
export const LOGIN_ROUTE = '/login';

// Staff route paths by key; each record path builds on its list path. Sport segments use the default sport's terms.
const defaultSportTerms = SPORT_TERMINOLOGY[Sport.default];
const STAFF_LIST_PATHS = {
  PLAYERS: `/${pluralTerm(defaultSportTerms[SportTerminology.ATHLETE].toLowerCase())}`,
  DRILLS: `/${pluralTerm(defaultSportTerms[SportTerminology.EVENT].toLowerCase())}`,
  SESSIONS: '/sessions',
  ASSIGNMENTS: '/assignments',
};

export const STAFF_PATHS = Object.freeze({
  ...STAFF_LIST_PATHS,
  PLAYER_ASSESSMENT: `${STAFF_LIST_PATHS.PLAYERS}/:playerId/:playerAssessmentId`,
  DRILL: `${STAFF_LIST_PATHS.DRILLS}/:drillId`,
  ASSESSMENT_SESSION: `${STAFF_LIST_PATHS.SESSIONS}/:assessmentSessionId`,
  CHECK_IN: '/check-in/:assessmentSessionId',
  EVALUATE: '/evaluate/:assessmentSessionId',
  AGE_GROUP: '/age-group/:ageGroupId',
  AGE_GROUP_REPORTS: '/reports/:ageGroupId',
  REPORT_CARD: '/report-card/:code/:playerAssessmentId',
  ASSIGNMENT: `${STAFF_LIST_PATHS.ASSIGNMENTS}/:assignmentId`,
});

interface ISelectionTargetRoute {
  path: string;
  params: Readonly<Record<string, keyof ISelectionTarget>>;
}

// Every staff route whose path names a record, mapped from path param to the target field it feeds.
export const SELECTION_TARGET_ROUTES: ReadonlyArray<ISelectionTargetRoute> = [
  { path: STAFF_PATHS.PLAYER_ASSESSMENT, params: { playerAssessmentId: 'playerAssessmentId' } },
  { path: STAFF_PATHS.REPORT_CARD, params: { playerAssessmentId: 'playerAssessmentId' } },
  { path: STAFF_PATHS.ASSESSMENT_SESSION, params: { assessmentSessionId: 'assessmentSessionId' } },
  { path: STAFF_PATHS.CHECK_IN, params: { assessmentSessionId: 'assessmentSessionId' } },
  { path: STAFF_PATHS.EVALUATE, params: { assessmentSessionId: 'assessmentSessionId' } },
  { path: STAFF_PATHS.AGE_GROUP, params: { ageGroupId: 'ageGroupId' } },
  { path: STAFF_PATHS.AGE_GROUP_REPORTS, params: { ageGroupId: 'ageGroupId' } },
  { path: STAFF_PATHS.DRILL, params: { drillId: 'drillId' } },
  { path: STAFF_PATHS.ASSIGNMENT, params: { assignmentId: 'calibrationAssignmentId' } },
];

// Separate protected tree under /parent — own passwordless login + separate ApolloClient.
// The `/parent` prefix distinguishes a parent session from a staff session at routing.
export const PARENT_HOME_ROUTE = '/parent';
export const PARENT_LOGIN_ROUTE = '/parent/login';
export const PARENT_ACCOUNT_ROUTE = '/parent/account';
// Deep link used by the welcome email + intro SMS; `section` opens the
// per-player contacts expander on arrival.
export const PARENT_ACCOUNT_SECTION_PARAM = 'section';
export const PARENT_ACCOUNT_CONTACTS_SECTION = 'contacts';
export const PARENT_CONTACT_DETAILS_ROUTE = `${PARENT_ACCOUNT_ROUTE}?${PARENT_ACCOUNT_SECTION_PARAM}=${PARENT_ACCOUNT_CONTACTS_SECTION}`;
export const PARENT_REPORT_CARD_ROUTE = '/parent/report-card';
export const PARENT_PLAYER_ROUTE = '/parent/player';
